import { computed, onScopeDispose, ref } from 'vue';

export const MIN_PANEL_WIDTH = 240;
export const MAX_PANEL_WIDTH = 320;
export const DEFAULT_PANEL_WIDTH = 288;
/** ドラッグで最小幅に着いてから、折りたたむまでにさらに必要な引き量 */
export const COLLAPSE_PULL = 120;

/**
 * ドラッグ中の幅。最小幅に着いたらいったん張り付き、そこからさらに
 * COLLAPSE_PULLだけ引いて初めて折りたたむ。
 * 最小幅で止めたいだけなのに勢いで閉じてしまうのを防ぐためのもので、
 * 手を離した時点の幅は必ず0か[最小幅, 最大幅]のいずれかになる。
 */
export const resolveDragWidth = (rawWidth: number): number => {
  if (rawWidth < MIN_PANEL_WIDTH - COLLAPSE_PULL) return 0;
  if (rawWidth < MIN_PANEL_WIDTH) return MIN_PANEL_WIDTH;
  return Math.min(rawWidth, MAX_PANEL_WIDTH);
};

export const resolvePanelWidth = (rawWidth: number): number => {
  if (rawWidth < MIN_PANEL_WIDTH) return 0;
  return Math.min(rawWidth, MAX_PANEL_WIDTH);
};

export const useResizableLeftMenu = (
  getStoredWidth: () => number | undefined,
  setStoredWidth: (width: number) => void,
) => {
  const stored = getStoredWidth();
  const panelWidth = ref(
    stored === undefined ? DEFAULT_PANEL_WIDTH : resolvePanelWidth(stored),
  );
  const lastExpandedWidth = ref(
    panelWidth.value === 0 ? DEFAULT_PANEL_WIDTH : panelWidth.value,
  );

  const isCollapsed = computed(() => panelWidth.value === 0);
  const isDragging = ref(false);

  const applyWidth = (rawWidth: number, persist = true) => {
    const width = resolvePanelWidth(rawWidth);
    if (width !== 0) lastExpandedWidth.value = width;
    panelWidth.value = width;
    if (persist) setStoredWidth(width);
  };

  const toggle = () => {
    applyWidth(isCollapsed.value ? lastExpandedWidth.value : 0);
  };

  /** 検索画面など、一時的に開くだけで永続化したくない場合に使う */
  const expandWithoutPersisting = () => {
    if (isCollapsed.value) applyWidth(lastExpandedWidth.value, false);
  };

  const restorePersistedWidth = () => {
    const persisted = getStoredWidth();
    panelWidth.value =
      persisted === undefined
        ? DEFAULT_PANEL_WIDTH
        : resolvePanelWidth(persisted);
  };

  let stopDrag: (() => void) | undefined;
  onScopeDispose(() => stopDrag?.(), true);

  /**
   * つまみへの `setPointerCapture` は Chromium が最初の pointermove で解除してしまい、
   * 以降の移動と pointerup を取り逃すため、window でドラッグを追う。
   * 押したままウィンドウを離れると pointerup が届かないことがあるので、
   * ボタンが離れた状態の pointermove でもドラッグを終える。
   */
  const startDrag = (event: PointerEvent) => {
    const startX = event.clientX;
    const startWidth = panelWidth.value;

    event.preventDefault();
    isDragging.value = true;

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.buttons === 0) {
        onEnd();
        return;
      }
      applyWidth(
        resolveDragWidth(startWidth + (moveEvent.clientX - startX)),
        false,
      );
    };

    const onEnd = () => {
      stopDrag = undefined;
      isDragging.value = false;
      setStoredWidth(panelWidth.value);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };

    stopDrag = onEnd;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };

  return {
    panelWidth,
    isCollapsed,
    isDragging,
    toggle,
    startDrag,
    expandWithoutPersisting,
    restorePersistedWidth,
  };
};
